import { useState } from 'react'
import { Plus, Edit2, Trash2, X, Target, AlertTriangle, TrendingUp } from 'lucide-react'

export default function BusinessContinuityPlanPage() {
  const [plans, setPlans] = useState([
    {
      id: 1,
      name: 'Primary Operations BCP',
      rto: 4,
      rpo: 1,
      criticality: 'critical',
      recovery: true,
      tested: true,
      lastTest: new Date().toLocaleDateString()
    },
    {
      id: 2,
      name: 'Secondary Systems BCP',
      rto: 8,
      rpo: 2,
      criticality: 'high',
      recovery: true,
      tested: true,
      lastTest: new Date().toLocaleDateString()
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    rto: 4,
    rpo: 1,
    criticality: 'high',
    recovery: true
  })

  const handleCreateClick = () => {
    setEditingPlan(null)
    setFormData({
      name: '',
      rto: 4,
      rpo: 1,
      criticality: 'high',
      recovery: true
    })
    setShowModal(true)
  }

  const handleEditPlan = (plan) => {
    setEditingPlan(plan)
    setFormData({
      name: plan.name,
      rto: plan.rto,
      rpo: plan.rpo,
      criticality: plan.criticality,
      recovery: plan.recovery
    })
    setShowModal(true)
  }

  const handleSavePlan = () => {
    if (!formData.name) {
      alert('Please fill in all required fields')
      return
    }

    if (editingPlan) {
      setPlans(plans.map(p =>
        p.id === editingPlan.id
          ? { ...p, ...formData, lastTest: new Date().toLocaleDateString() }
          : p
      ))
    } else {
      const newPlan = {
        id: Date.now(),
        ...formData,
        tested: false,
        lastTest: 'Not tested'
      }
      setPlans([...plans, newPlan])
    }

    setShowModal(false)
  }

  const handleDeletePlan = (id) => {
    if (confirm('Are you sure you want to delete this plan?')) {
      setPlans(plans.filter(p => p.id !== id))
    }
  }

  const getCriticalityColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-600/20 text-red-400'
      case 'high': return 'bg-orange-600/20 text-orange-400'
      case 'medium': return 'bg-yellow-600/20 text-yellow-400'
      default: return 'bg-green-600/20 text-green-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Business Continuity Plan</h1>
          </div>
          <p className="text-slate-400">Manage Recovery Time Objectives (RTO) and Recovery Point Objectives (RPO) for critical business systems</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Plans</div>
            <div className="text-3xl font-bold text-blue-400">{plans.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg RTO</div>
            <div className="text-3xl font-bold text-cyan-400">{Math.round(plans.reduce((sum, p) => sum + p.rto, 0) / plans.length)} hrs</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg RPO</div>
            <div className="text-3xl font-bold text-green-400">{Math.round(plans.reduce((sum, p) => sum + p.rpo, 0) / plans.length)} hrs</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Tested Plans</div>
            <div className="text-3xl font-bold text-yellow-400">{plans.filter(p => p.tested).length}</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create BCP
        </button>

        {/* Plans List */}
        <div className="space-y-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-bold ${getCriticalityColor(plan.criticality)}`}>
                      {plan.criticality.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">RTO (Recovery Time Objective)</span>
                      <div className="text-white font-semibold text-lg mt-1">{plan.rto} hours</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">RPO (Recovery Point Objective)</span>
                      <div className="text-white font-semibold text-lg mt-1">{plan.rpo} hours</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">Recovery Procedure</span>
                      <div className={`font-semibold text-lg mt-1 ${plan.recovery ? 'text-green-400' : 'text-red-400'}`}>
                        {plan.recovery ? 'Documented' : 'Pending'}
                      </div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">Last Tested</span>
                      <div className={`font-semibold text-lg mt-1 ${plan.tested ? 'text-green-400' : 'text-yellow-400'}`}>
                        {plan.lastTest}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditPlan(plan)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeletePlan(plan.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingPlan ? 'Edit Business Continuity Plan' : 'Create Business Continuity Plan'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Plan Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., Primary Operations BCP"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">RTO (Hours)</label>
                    <input
                      type="number"
                      value={formData.rto}
                      onChange={(e) => setFormData({ ...formData, rto: parseInt(e.target.value) })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">RPO (Hours)</label>
                    <input
                      type="number"
                      value={formData.rpo}
                      onChange={(e) => setFormData({ ...formData, rpo: parseInt(e.target.value) })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Criticality</label>
                    <select
                      value={formData.criticality}
                      onChange={(e) => setFormData({ ...formData, criticality: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.recovery}
                    onChange={(e) => setFormData({ ...formData, recovery: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 text-blue-600 bg-slate-700 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-white font-semibold">Recovery procedure documented</span>
                </label>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePlan}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
